import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { addStoryComment, fetchStoryComments, toggleStoryCommentLike, toggleStoryLike } from '../services/api';
import MobileBottomSheet from './MobileBottomSheet';

const DEFAULT_AVATAR = '/default-avatar.svg';

const REPORT_REASONS = [
  'Contenu inapproprié',
  'Spam ou publicité',
  "Droits d'auteur",
  'Violence ou haine',
  'Harcèlement',
  'Autre'
];

const formatTimeAgo = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `${diffMin} min`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours} h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} j`;
  return date.toLocaleDateString('fr-FR');
};

const buildCommentTree = (comments) => {
  const map = new Map();
  const roots = [];
  comments.forEach((comment) => {
    map.set(comment.id, { ...comment, replies: [] });
  });
  comments.forEach((comment) => {
    const node = map.get(comment.id);
    if (comment.parent_id && map.has(comment.parent_id)) {
      map.get(comment.parent_id).replies.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
};

export default function StoryViewer({ stories = [], startIndex = 0, onClose, resolveProfileImage, resolveImageUrl, shouldPause = false, onStoryPlay, user, onReportStory, onDeleteStory, onRequireAuth }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const NativeShare = registerPlugin('NativeShare');
  const FRONTEND_URL = useMemo(() => {
    const env = (process.env.REACT_APP_FRONTEND_URL || '').replace(/\/+$/, '');
    const origin = typeof window !== 'undefined' ? window.location.origin.replace(/\/+$/, '') : '';
    const isNativeOrigin = origin.startsWith('capacitor://') || origin.startsWith('http://localhost') || origin.startsWith('http://127.0.0.1');
    if (isNativeOrigin && env) return env;
    if (origin && env && origin !== env) {
      return origin;
    }
    return env || origin;
  }, []);
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [viewCounts, setViewCounts] = useState({});
  const videoRef = useRef(null);
  const viewedRef = useRef(new Set());
  const frameRef = useRef(null);
  const [frameBounds, setFrameBounds] = useState({ width: 0, height: 0 });
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportTargetId, setReportTargetId] = useState(null);
  const [likeState, setLikeState] = useState({ liked: false, count: 0 });
  const [commentCount, setCommentCount] = useState(0);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState('');
  const [replyInput, setReplyInput] = useState('');
  const [openReplies, setOpenReplies] = useState({});
  const [replyTo, setReplyTo] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const shareCopiedTimerRef = useRef(null);
  const wasPlayingBeforeCommentsRef = useRef(false);
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const swipeHandledRef = useRef(false);
  const [isMobile, setIsMobile] = useState(false);
  const [bottomNavHeight, setBottomNavHeight] = useState(0);

  const currentStory = stories[currentIndex];
  const isOwner = !!(user && currentStory && Number(currentStory.user_id) === Number(user.id));
  const hasMusic = !!(currentStory && (currentStory.music_title || currentStory.music_artist));
  const videoType = useMemo(() => {
    const url = (currentStory?.video_url || '').split('?')[0].toLowerCase();
    if (url.endsWith('.mp4')) return 'video/mp4';
    if (url.endsWith('.mov')) return 'video/quicktime';
    if (url.endsWith('.webm') || url.endsWith('.bin')) return 'video/webm';
    return undefined;
  }, [currentStory?.video_url]);
  const storyFormat = useMemo(() => {
    const wRaw = Number(currentStory?.format_width);
    const hRaw = Number(currentStory?.format_height);
    if (Number.isFinite(wRaw) && Number.isFinite(hRaw) && wRaw > 0 && hRaw > 0) {
      return { w: wRaw, h: hRaw };
    }
    const id = currentStory?.format_id;
    if (id === 'mobile-9-16') return { w: 9, h: 16 };
    if (id === 'landscape-16-9') return { w: 16, h: 9 };
    if (id === 'square-1-1') return { w: 1, h: 1 };
    return null;
  }, [currentStory?.format_id, currentStory?.format_width, currentStory?.format_height]);
  const videoFitClass = useMemo(() => {
    if (!storyFormat) return 'object-contain';
    return storyFormat.h >= storyFormat.w ? 'object-cover' : 'object-contain';
  }, [storyFormat]);
  const posterSrc = useMemo(() => {
    if (!currentStory?.thumbnail_url) return null;
    if (currentStory.thumbnail_url.startsWith('http://') || currentStory.thumbnail_url.startsWith('https://')) {
      return currentStory.thumbnail_url;
    }
    const filename = currentStory.thumbnail_url.split('/').pop();
    return `${BACKEND_URL}/api/images/${filename}`;
  }, [currentStory?.thumbnail_url, BACKEND_URL]);
  const effectiveViews = useMemo(() => {
    if (!currentStory) return 0;
    const override = viewCounts[currentStory.id];
    return override !== undefined ? override : (currentStory.views || 0);
  }, [currentStory, viewCounts]);
  const shareUrl = useMemo(() => {
    if (!currentStory) return '';
    const baseRaw = (process.env.REACT_APP_SHARE_BASE || BACKEND_URL || FRONTEND_URL || '').replace(/\/+$/, '');
    const base = baseRaw.startsWith('capacitor://') ? '' : baseRaw;
    return base ? `${base}/api/share/story/${currentStory.id}` : '';
  }, [currentStory, BACKEND_URL, FRONTEND_URL]);
  const commentTree = useMemo(() => buildCommentTree(comments), [comments]);
  const effectiveBottomNavHeight = useMemo(() => {
    if (!isMobile) return 0;
    return bottomNavHeight || 64;
  }, [bottomNavHeight, isMobile]);

  const bottomNavOffset = useMemo(
    () => (isMobile ? '0px' : '0px'),
    [isMobile]
  );

  const viewerSize = useMemo(() => {
    if (isMobile) {
      const heightValue = effectiveBottomNavHeight
        ? `calc(100% - ${effectiveBottomNavHeight}px)`
        : '100%';
      return {
        width: frameBounds.width || '100%',
        height: heightValue
      };
    }
    const ratio = 9 / 16;
    if (!frameBounds.width || !frameBounds.height) {
      return { width: null, height: null };
    }
    const maxWidth = frameBounds.width * 0.92;
    const maxHeight = frameBounds.height * 0.92;
    let width = maxWidth;
    let height = width / ratio;
    if (height > maxHeight) {
      height = maxHeight;
      width = height * ratio;
    }
    return { width, height };
  }, [frameBounds, isMobile, effectiveBottomNavHeight]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const apply = () => setIsMobile(!!mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else mq.removeListener(apply);
    };
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setBottomNavHeight(0);
      return;
    }
    const update = () => {
      const nav = document.querySelector('.mobile-bottom-nav');
      const height = nav?.getBoundingClientRect().height || 0;
      setBottomNavHeight(height);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isMobile]);

  const handleFrameClick = () => {
    if (swipeHandledRef.current) {
      swipeHandledRef.current = false;
      return;
    }
    const v = videoRef.current;
    if (!v) return;
    if (!v.paused) {
      v.pause();
      return;
    }
    if (typeof onStoryPlay === 'function') onStoryPlay();
    const tryPlay = () => {
      try {
        const playPromise = v.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } catch (_) {}
    };
    if (shouldPause) {
      window.setTimeout(tryPlay, 60);
    } else {
      tryPlay();
    }
  };

  const handleTouchStart = (e) => {
    if (!isMobile) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
  };

  const handleTouchEnd = (e) => {
    if (!isMobile) return;
    const touch = e.changedTouches?.[0];
    if (!touch) return;
    const { x, y } = touchStartRef.current || {};
    const dx = touch.clientX - x;
    const dy = touch.clientY - y;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    const threshold = 60;
    if (absY > absX && absY > threshold) {
      swipeHandledRef.current = true;
      if (dy < 0) {
        handleNext();
      } else {
        handlePrev();
      }
      setTimeout(() => {
        swipeHandledRef.current = false;
      }, 120);
    }
  };

  useEffect(() => {
    if (!currentStory) return;
    setLikeState({
      liked: Number(currentStory.user_liked) === 1,
      count: Number(currentStory.like_count || 0)
    });
    setCommentCount(Number(currentStory.comment_count || 0));
    setComments([]);
    setCommentsOpen(false);
    setReplyTo(null);
    setCommentInput('');
    setReplyInput('');
    setOpenReplies({});
    setShareCopied(false);
  }, [currentStory?.id]);

  const handleNext = () => {
    if (currentIndex < stories.length - 1) {
      setCurrentIndex((idx) => idx + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((idx) => idx - 1);
    }
  };

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      } else if (event.key === 'ArrowDown') {
        handleNext();
      } else if (event.key === 'ArrowUp') {
        handlePrev();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentIndex, stories.length]);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;

    const updateBounds = () => {
      const rect = el.getBoundingClientRect();
      setFrameBounds({ width: rect.width, height: rect.height });
    };

    updateBounds();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateBounds);
      return () => window.removeEventListener('resize', updateBounds);
    }

    const observer = new ResizeObserver(() => updateBounds());
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setReportTargetId(null);
    const videoEl = videoRef.current;
    if (!videoEl) return;

    setProgress(0);
    try {
      videoEl.muted = isMuted;
    } catch (_) {}
    try {
      videoEl.pause();
      videoEl.load();
    } catch (_) {}

    const attemptPlay = () => {
      if (shouldPause) return;
      try {
        const playPromise = videoEl.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            try {
              videoEl.muted = true;
              setIsMuted(true);
              videoEl.play().catch(() => {});
            } catch (_) {}
          });
        }
        if (typeof onStoryPlay === 'function') {
          onStoryPlay();
        }
      } catch (_) {}
    };

    const handleReady = () => {
      try {
        videoEl.currentTime = 0;
      } catch (_) {}
      attemptPlay();
    };

    videoEl.addEventListener('loadeddata', handleReady, { once: true });
    videoEl.addEventListener('canplay', handleReady, { once: true });
    attemptPlay();
  }, [currentIndex, shouldPause, onStoryPlay]);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    if (shouldPause) {
      try { videoEl.pause(); } catch (_) {}
      return;
    }
    if (videoEl.paused) {
      try {
        const playPromise = videoEl.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
        if (typeof onStoryPlay === 'function') {
          onStoryPlay();
        }
      } catch (_) {}
    }
  }, [shouldPause, currentIndex, onStoryPlay]);

  useEffect(() => {
    if (!currentStory) return;
    if (viewedRef.current.has(currentStory.id)) return;
    viewedRef.current.add(currentStory.id);

    fetch(`${BACKEND_URL}/api/stories/${currentStory.id}/view`, {
      method: 'POST'
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data && typeof data.views === 'number') {
          setViewCounts((prev) => ({ ...prev, [currentStory.id]: data.views }));
        } else {
          setViewCounts((prev) => ({
            ...prev,
            [currentStory.id]: (currentStory.views || 0) + 1
          }));
        }
      })
      .catch(() => {
        setViewCounts((prev) => ({
          ...prev,
          [currentStory.id]: (currentStory.views || 0) + 1
        }));
      });
  }, [currentStory, BACKEND_URL]);

  useEffect(() => {
    setCurrentIndex(startIndex);
    setIsMuted(true);
    if (videoRef.current) {
      try { videoRef.current.muted = true; } catch (_) {}
    }
  }, [startIndex]);

  const requireAuth = () => {
    if (typeof onRequireAuth === 'function') {
      onRequireAuth();
    } else {
      alert('Tu dois être connecté.');
    }
  };

  const loadComments = async () => {
    if (!currentStory) return;
    setCommentsLoading(true);
    try {
      const data = await fetchStoryComments(currentStory.id);
      const rawList = Array.isArray(data) ? data : (Array.isArray(data?.comments) ? data.comments : []);
      const list = rawList.map((item) => ({
        ...item,
        like_count: Number(item.like_count || 0),
        reply_count: Number(item.reply_count || 0),
        user_liked: Number(item.user_liked) === 1
      }));
      setComments(list);
      setCommentCount(list.length);
    } catch (error) {
      console.error('Erreur chargement commentaires:', error);
    } finally {
      setCommentsLoading(false);
    }
  };

  useEffect(() => {
    if (commentsOpen) {
      loadComments();
    }
  }, [commentsOpen, currentStory?.id]);

  useEffect(() => {
    if (!commentsOpen) {
      setReplyTo(null);
      setReplyInput('');
    }
  }, [commentsOpen, currentStory?.id]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (commentsOpen) {
      const isPlaying = !video.paused && !video.ended;
      wasPlayingBeforeCommentsRef.current = isPlaying;
      if (isPlaying) {
        try { video.pause(); } catch (_) {}
      }
    } else if (wasPlayingBeforeCommentsRef.current) {
      wasPlayingBeforeCommentsRef.current = false;
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    }
  }, [commentsOpen]);

  const handleToggleLike = async (e) => {
    e.stopPropagation();
    if (!currentStory) return;
    if (!user) {
      requireAuth();
      return;
    }
    try {
      setLikeState((prev) => {
        const nextLiked = !prev.liked;
        const nextCount = Math.max(0, prev.count + (nextLiked ? 1 : -1));
        return { liked: nextLiked, count: nextCount };
      });
      const data = await toggleStoryLike(currentStory.id);
      setLikeState({
        liked: !!data?.liked,
        count: Number(data?.like_count || 0)
      });
    } catch (error) {
      console.error('Erreur like story:', error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        requireAuth();
      }
    }
  };

  const handleSubmitComment = async () => {
    if (!currentStory) return;
    if (!user) {
      requireAuth();
      return;
    }
    const content = commentInput.trim();
    if (!content) return;
    try {
      const newComment = await addStoryComment(currentStory.id, content, null);
      setComments((prev) => [...prev, newComment]);
      setCommentInput('');
      setCommentCount((prev) => prev + 1);
      await loadComments();
    } catch (error) {
      console.error('Erreur ajout commentaire:', error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        requireAuth();
      } else {
        alert("Impossible d'ajouter le commentaire.");
      }
    }
  };

  const handleSubmitReply = async () => {
    if (!currentStory || !replyTo) return;
    if (!user) {
      requireAuth();
      return;
    }
    const content = replyInput.trim();
    if (!content) return;
    try {
      const newComment = await addStoryComment(currentStory.id, content, replyTo.id);
      setComments((prev) => {
        const updated = prev.map((c) =>
          c.id === replyTo.id ? { ...c, reply_count: (c.reply_count || 0) + 1 } : c
        );
        return [...updated, newComment];
      });
      setOpenReplies((prev) => ({ ...prev, [replyTo.id]: true }));
      setReplyInput('');
      setReplyTo(null);
      setCommentCount((prev) => prev + 1);
      await loadComments();
    } catch (error) {
      console.error('Erreur ajout réponse:', error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        requireAuth();
      } else {
        alert("Impossible d'ajouter la réponse.");
      }
    }
  };

  const handleToggleCommentLike = async (commentId) => {
    if (!user) {
      requireAuth();
      return;
    }
    try {
      const data = await toggleStoryCommentLike(commentId);
      setComments((prev) =>
        prev.map((comment) =>
          comment.id === commentId
            ? { ...comment, like_count: data.like_count || 0, user_liked: !!data.liked }
            : comment
        )
      );
    } catch (error) {
      console.error('Erreur like commentaire:', error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        requireAuth();
      }
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    if (!shareUrl) return;
    try {
      const safeShareUrl = /^https?:\/\//i.test(shareUrl) ? shareUrl : '';
      const sharePayload = {
        title: currentStory?.title || 'Vinisys Music',
        text: currentStory?.title ? `Story: ${currentStory.title}` : 'Story sur Vinisys Music',
        ...(safeShareUrl ? { url: safeShareUrl } : {})
      };
      const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
      const platform = Capacitor?.getPlatform?.() || 'web';
      const isNative = platform !== 'web';
      const canNativeIosShare = platform === 'ios' && Capacitor?.isPluginAvailable?.('NativeShare');
      if (canNativeIosShare) {
        await NativeShare.share(sharePayload);
        return;
      }
      const canCapShare = !!(isNative && Capacitor?.isPluginAvailable?.('Share'));
      if (canCapShare) {
        await Share.share(sharePayload);
        return;
      }
      if (isNative) {
        return;
      }
      if (canNativeShare) {
        await navigator.share(sharePayload);
        return;
      }
      if (isMobile) {
        // Sur mobile, ne pas copier le lien si le partage natif n'est pas dispo.
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      if (shareCopiedTimerRef.current) {
        clearTimeout(shareCopiedTimerRef.current);
      }
      shareCopiedTimerRef.current = setTimeout(() => setShareCopied(false), 1500);
    } catch (_) {
      try {
        const temp = document.createElement('input');
        temp.value = shareUrl;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
        setShareCopied(true);
        if (shareCopiedTimerRef.current) {
          clearTimeout(shareCopiedTimerRef.current);
        }
        shareCopiedTimerRef.current = setTimeout(() => setShareCopied(false), 1500);
      } catch (err) {
        console.error('Erreur copie lien:', err);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (shareCopiedTimerRef.current) {
        clearTimeout(shareCopiedTimerRef.current);
      }
    };
  }, []);

  const openShareLink = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const countAllReplies = (node) => {
    if (!node?.replies || node.replies.length === 0) return 0;
    return node.replies.reduce((sum, reply) => sum + 1 + countAllReplies(reply), 0);
  };

  const renderComment = (comment, depth = 0) => {
    const avatar = comment.user_profile_image
      ? (resolveProfileImage ? resolveProfileImage(comment.user_profile_image) : comment.user_profile_image)
      : DEFAULT_AVATAR;
    const hasReplies = Array.isArray(comment.replies) && comment.replies.length > 0;
    const isExpanded = !!openReplies[comment.id];
    const totalReplies = hasReplies ? countAllReplies(comment) : 0;
    return (
      <div key={comment.id} className="space-y-3">
        <div className="flex gap-3">
          <div className="w-9 h-9 rounded-full overflow-hidden bg-white/10 flex items-center justify-center text-xs flex-shrink-0">
            <img src={avatar} alt={comment.user_name} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <span className="truncate">{comment.user_name || 'Utilisateur'}</span>
              <span className="text-xs text-white/40">{formatTimeAgo(comment.created_at)}</span>
            </div>
            <div className="text-sm text-white/90 mt-1 whitespace-pre-wrap">{comment.content}</div>
            <div className="flex items-start gap-4 text-xs text-white/60 mt-2">
              <div className="flex flex-col items-start gap-1">
                <button
                  type="button"
                  onClick={() => {
                    setReplyTo(comment);
                    setReplyInput('');
                    setOpenReplies((prev) => ({ ...prev, [comment.id]: true }));
                  }}
                  className="text-purple-300 hover:text-purple-200"
                >
                  Répondre
                </button>
                {totalReplies > 0 && (
                  <button
                    type="button"
                    onClick={() => setOpenReplies((prev) => ({ ...prev, [comment.id]: !prev[comment.id] }))}
                    className="text-white/60 hover:text-white"
                  >
                    {isExpanded
                      ? `Masquer ${totalReplies} ${totalReplies > 1 ? 'réponses' : 'réponse'}`
                      : `Voir ${totalReplies} ${totalReplies > 1 ? 'réponses' : 'réponse'}`}
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleToggleCommentLike(comment.id)}
                className={`inline-flex items-center gap-1 ${comment.user_liked ? 'text-purple-300' : 'hover:text-white'}`}
              >
                ♥ <span>{comment.like_count || 0}</span>
              </button>
            </div>
          </div>
        </div>

        {replyTo?.id === comment.id && (
          <div className="relative pl-8">
            <div className="absolute left-2 top-4 w-6 h-px bg-white/15" />
            <div className="flex flex-wrap items-center gap-2">
                <div className="w-8 h-8 rounded-full overflow-hidden bg-white/10 flex items-center justify-center text-xs flex-shrink-0">
                  <img
                    src={user?.profile_image ? (resolveProfileImage ? resolveProfileImage(user.profile_image) : user.profile_image) : DEFAULT_AVATAR}
                    alt={user?.name || 'Utilisateur'}
                    className="w-full h-full object-cover"
                  />
                </div>
                <input
                  type="text"
                  value={replyInput}
                  onChange={(e) => setReplyInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmitReply();
                    }
                  }}
                  placeholder={`Répondre à ${comment.user_name || 'Utilisateur'}`}
                  className="flex-1 min-w-[160px] bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-purple-500/60"
                />
                <button
                  type="button"
                  onClick={handleSubmitReply}
                  aria-label="Envoyer"
                  className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white flex items-center justify-center shadow-lg hover:from-purple-700 hover:to-pink-700 shrink-0"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                    <path d="M2 21l20-9L2 3v7l14 2-14 2v7z"/>
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setReplyTo(null);
                    setReplyInput('');
                  }}
                  className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-xs shrink-0"
                  title="Annuler"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

        {hasReplies && isExpanded && (
          <div className="relative mt-2 pl-8">
            <div className="absolute left-2 top-0 bottom-0 w-px bg-white/15" />
            <div className="space-y-3">
              {comment.replies.map((reply) => (
                <div key={reply.id} className="relative">
                  <div className="absolute left-2 top-5 w-6 h-px bg-white/15" />
                  {renderComment(reply, depth + 1)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!currentStory) {
    return (
      <div className="flex-1 flex items-center justify-center text-white/60">
        Aucune story à afficher.
      </div>
    );
  }

  return (
    <div className={`h-full w-full flex ${isMobile ? 'items-stretch justify-stretch' : 'items-center justify-center'}`}>
      <div ref={frameRef} className={`relative w-full h-full flex ${isMobile ? 'items-stretch justify-stretch' : 'items-center justify-center'}`}>
        <div
          className={`relative bg-black overflow-hidden ${isMobile ? 'rounded-none shadow-none' : 'rounded-2xl shadow-2xl'}`}
          style={viewerSize.width ? { width: viewerSize.width, height: viewerSize.height } : { width: 'min(480px,92vw)', aspectRatio: '9 / 16' }}
          onClick={handleFrameClick}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <video
            key={currentStory?.id || 'story-video'}
            ref={videoRef}
            className={`absolute inset-0 w-full h-full ${videoFitClass} bg-black`}
            autoPlay
            playsInline
            muted={isMuted}
            poster={posterSrc || undefined}
            onPlay={() => {
              if (typeof onStoryPlay === 'function') onStoryPlay();
            }}
            onTimeUpdate={(e) => {
              const el = e.currentTarget;
              if (!el.duration || Number.isNaN(el.duration)) return;
              setProgress(Math.min(1, el.currentTime / el.duration));
            }}
            onEnded={handleNext}
          >
            <source src={currentStory.video_url} type={videoType} />
          </video>

          <button
            onClick={(e) => {
              e.stopPropagation();
              const nextMuted = !isMuted;
              setIsMuted(nextMuted);
              if (videoRef.current) {
                videoRef.current.muted = nextMuted;
              }
            }}
            className="absolute top-3 left-3 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 border border-white/10 text-white/80 flex items-center justify-center"
            title={isMuted ? 'Activer le son' : 'Couper le son'}
          >
            {isMuted ? '🔇' : '🔊'}
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            className="absolute top-3 right-14 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 border border-white/10 text-white/80 flex items-center justify-center"
            title="Options"
          >
            ⋯
          </button>

          {menuOpen && (
            <div
              className="absolute top-14 right-3 z-20 min-w-[180px] bg-[#14141f] border border-white/10 rounded-lg shadow-xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {!isOwner && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setReportTargetId(currentStory?.id || null);
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-white/10 text-white/80"
                >
                  Signaler
                </button>
              )}
              {isOwner && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    if (currentStory?.id) onDeleteStory?.(currentStory.id);
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-white/10 text-red-300"
                >
                  Supprimer
                </button>
              )}
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-white/10 text-white/60"
              >
                Fermer
              </button>
            </div>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
            className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 border border-white/10 text-white/80 flex items-center justify-center"
            title="Fermer"
          >
            ✕
          </button>

          <div
            className="absolute left-0 right-0 px-4 pt-10 bg-gradient-to-t from-black/90 via-black/40 to-transparent"
            style={
              isMobile
                ? { bottom: bottomNavOffset, paddingBottom: '24px' }
                : { bottom: '0px', paddingBottom: '24px' }
            }
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded-full overflow-hidden bg-white/10 flex items-center justify-center text-xs">
                <img
                  src={currentStory.user_profile_image ? (resolveProfileImage ? resolveProfileImage(currentStory.user_profile_image) : currentStory.user_profile_image) : DEFAULT_AVATAR}
                  alt={currentStory.user_name || 'Utilisateur'}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="text-sm font-semibold truncate">
                {currentStory.user_name || 'Utilisateur'}
              </div>
            </div>
            {!hasMusic && (
              <div className="text-xs text-white/60 mb-2">
                {effectiveViews} vues
              </div>
            )}

            {hasMusic && (
              <div className="flex items-center gap-3 bg-[#2b2b35]/90 border border-white/10 rounded-2xl px-3 py-2">
                <div className="w-11 h-11 rounded-full overflow-hidden bg-white/10 flex items-center justify-center">
                  {currentStory.music_cover_image ? (
                    <img
                      src={resolveImageUrl ? resolveImageUrl(currentStory.music_cover_image) : currentStory.music_cover_image}
                      alt={currentStory.music_title || 'Musique'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-lg">🎵</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">
                    {currentStory.music_title || 'Musique de fond'}
                  </div>
                  <div className="text-xs text-white/60 truncate">
                    {currentStory.music_artist || 'Artiste inconnu'} • {effectiveViews} vues
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            className="absolute left-0 right-0 h-1 bg-white/10"
            style={{ bottom: isMobile ? bottomNavOffset : '0px' }}
          >
            <div className="h-full bg-white/80 transition-all" style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
        </div>

        <div className="absolute right-6 top-1/2 -translate-y-1/2 flex flex-col items-center gap-2">
          <button
            onClick={handlePrev}
            disabled={currentIndex <= 0}
            className={`w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-white/80 transition ${
              currentIndex <= 0 ? 'opacity-30 cursor-not-allowed' : 'bg-black/50 hover:bg-black/70'
            }`}
            title="Story précédente"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 19V5" />
              <path d="M5 12l7-7 7 7" />
            </svg>
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex >= stories.length - 1}
            className={`w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-white/80 transition ${
              currentIndex >= stories.length - 1 ? 'opacity-30 cursor-not-allowed' : 'bg-black/50 hover:bg-black/70'
            }`}
            title="Story suivante"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14" />
              <path d="M5 12l7 7 7-7" />
            </svg>
          </button>

          <button
            onClick={handleToggleLike}
            className={`w-12 h-12 rounded-full border flex items-center justify-center transition ${
              likeState.liked
                ? 'bg-purple-500/25 text-purple-200 border-purple-400/40'
                : 'bg-black/50 text-white/80 border-white/10 hover:bg-black/70'
            }`}
            title="J'aime"
          >
            <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
              <path d="M12 21s-7.5-4.4-9.5-8.4C.9 9.1 2.4 6 5.6 5.2c2-.5 3.9.3 5.1 1.7 1.2-1.4 3.1-2.2 5.1-1.7 3.2.8 4.7 3.9 3.1 7.4C19.5 16.6 12 21 12 21z"/>
            </svg>
          </button>
          <div className="text-xs text-white/80 font-bold -mt-2">{likeState.count}</div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setCommentsOpen(true);
            }}
            className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-white/80 bg-black/50 hover:bg-black/70 transition"
            title="Commentaires"
          >
            <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
              <path d="M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-5 5v-5H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/>
            </svg>
          </button>
          <div className="text-xs text-white/80 font-bold -mt-2">{commentCount}</div>

          <button
            onClick={handleShare}
            className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-white/80 bg-black/50 hover:bg-black/70 transition"
            title="Partager"
          >
            <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
              <path d="M18 8a3 3 0 0 0-2.4 1.2L8.9 7.2A3 3 0 0 0 9 6a3 3 0 1 0-1.1 2.3l6.6 2a3 3 0 0 0 0 3.4l-6.6 2A3 3 0 1 0 9 18a3 3 0 0 0-.1-1.2l6.7-2.1A3 3 0 1 0 18 8z"/>
            </svg>
          </button>
          <div className="text-[10px] text-white/70 font-bold -mt-2">{shareCopied ? 'Copié' : 'PARTAGER'}</div>
        </div>
        {shareCopied && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="px-5 py-3 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold shadow-lg">
              Lien de partage copié
            </div>
          </div>
        )}
      </div>

      {commentsOpen && (
        isMobile ? (
          <MobileBottomSheet
            isOpen={commentsOpen}
            onClose={() => setCommentsOpen(false)}
            sheetClassName="min-h-[70vh] max-h-[70vh]"
            sheetStyle={{ height: '70vh' }}
            contentClassName="max-h-[calc(70vh-16px)]"
          >
            <div className="flex flex-col min-h-[50vh] text-white">
              <div className="flex items-center justify-between pb-3 border-b border-white/10">
                <h3 className="text-lg font-semibold">Commentaires</h3>
                <button
                  type="button"
                  onClick={() => setCommentsOpen(false)}
                  className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
                >
                  ✕
                </button>
              </div>

              <div className="flex-1 py-4 space-y-4">
                {commentsLoading ? (
                  <div className="text-sm text-white/60">Chargement...</div>
                ) : commentTree.length === 0 ? (
                  <div className="text-sm text-white/60">Aucun commentaire pour le moment.</div>
                ) : (
                  commentTree.map((comment) => renderComment(comment))
                )}
              </div>

              <div className="sticky bottom-0 -mx-4 px-4 pt-3 pb-4 bg-[#1c1c1f] border-t border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 flex items-center justify-center text-xs">
                    <img
                      src={user?.profile_image ? (resolveProfileImage ? resolveProfileImage(user.profile_image) : user.profile_image) : DEFAULT_AVATAR}
                      alt={user?.name || 'Utilisateur'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <input
                    type="text"
                    value={commentInput}
                    onChange={(e) => setCommentInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSubmitComment();
                      }
                    }}
                    placeholder="Écrire un commentaire"
                    className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-purple-500/60"
                  />
                  <button
                    type="button"
                    onClick={handleSubmitComment}
                    aria-label="Envoyer"
                    className="w-11 h-11 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white flex items-center justify-center shadow-lg hover:from-purple-700 hover:to-pink-700"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                      <path d="M2 21l20-9L2 3v7l14 2-14 2v7z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </MobileBottomSheet>
        ) : (
          <div className="fixed inset-0 z-[1100] flex justify-end">
            <div
              className="absolute inset-0 bg-black/60"
              onClick={() => setCommentsOpen(false)}
            />
            <div className="relative h-full w-full max-w-[420px] bg-[#14141f] border-l border-white/10 flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                <h3 className="text-lg font-semibold">Commentaires</h3>
                <button
                  type="button"
                  onClick={() => setCommentsOpen(false)}
                  className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
                >
                  ✕
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                {commentsLoading ? (
                  <div className="text-sm text-white/60">Chargement...</div>
                ) : commentTree.length === 0 ? (
                  <div className="text-sm text-white/60">Aucun commentaire pour le moment.</div>
                ) : (
                  commentTree.map((comment) => renderComment(comment))
                )}
              </div>

              <div className="border-t border-white/10 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 flex items-center justify-center text-xs">
                    <img
                      src={user?.profile_image ? (resolveProfileImage ? resolveProfileImage(user.profile_image) : user.profile_image) : DEFAULT_AVATAR}
                      alt={user?.name || 'Utilisateur'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <input
                    type="text"
                    value={commentInput}
                    onChange={(e) => setCommentInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSubmitComment();
                      }
                    }}
                    placeholder="Écrire un commentaire"
                    className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-purple-500/60"
                  />
                  <button
                    type="button"
                    onClick={handleSubmitComment}
                    className="px-4 py-2 rounded-full bg-purple-500/80 hover:bg-purple-500 text-sm font-semibold"
                  >
                    Envoyer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {reportTargetId && (
        <div className="fixed inset-0 z-[1200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#14141f] border border-white/10 rounded-2xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Signaler une story</h3>
              <button
                onClick={() => setReportTargetId(null)}
                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-white/60 mb-4">
              Choisis un motif de signalement.
            </p>
            <div className="space-y-2">
              {REPORT_REASONS.map((reason) => (
                <button
                  key={reason}
                  onClick={() => {
                    onReportStory?.(reportTargetId, reason);
                    setReportTargetId(null);
                  }}
                  className="w-full text-left px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm"
                >
                  {reason}
                </button>
              ))}
            </div>
            {!user && (
              <div className="mt-3 text-xs text-red-300">
                Tu dois être connecté pour signaler une story.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
